import { LoadingButton } from "@mui/lab";
import { Box, Grid, Typography } from "@mui/material";
import { useGoogleLogin } from "@react-oauth/google";
import { AlertContext } from "contexts/Alert";
import { FairPaySurveyResponseContext } from "contexts/FairPaySurveyResponse";
import { useContext, useState } from "react";

import { FairPaySurveyResponse } from "features/FairPaySurvey/schemas";
import { fetcher } from "utils/api";

const styles = {
  badge: {
    my: 2,
    background: "#151F16",
    p: 5,
    borderRadius: "20px",
  },
  descriptionLargeText: {
    textAlign: "center",
    fontSize: "28px",
    color: "white",
    fontWeight: 600,
    mb: 2,
  },
  descriptionText: {
    fontSize: "16px",
    textAlign: "center",
    color: "white",
  },
  ctaBox: {
    p: 3,
    mt: 3,
    background: "white",
    borderRadius: "20px",
  },
  button: {
    px: 3,
    py: 2,
    textTransform: "none",
    borderRadius: 20,
    my: 2,
  },
};

export default function FullReportCTA({
  survey,
}: {
  survey: FairPaySurveyResponse;
}) {
  const { setAlert } = useContext(AlertContext);
  const { setFairPaySurveyResponse } = useContext(FairPaySurveyResponseContext);
  const [loading, setLoading] = useState<boolean>(false);

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);

        const googleResponse = await fetcher(
          `/api/organization/fair-pay-survey-responses/${survey.surveyUuid}/oauth/callback`,
          "POST",
          {},
          tokenResponse,
        );
        setFairPaySurveyResponse(googleResponse.surveyResponse);

        const generateResponse = await fetcher(
          `/api/organization/fair-pay-survey-responses/${survey.surveyUuid}/generate`,
          "POST",
        );
        setFairPaySurveyResponse(generateResponse.surveyResponse);
        setAlert(
          "Successfully connected! We will send you an email when the report is complete",
          "success",
        );
      } catch (error) {
        setAlert(
          error.message ? error.message : "Unable to connect with Google",
          "error",
        );
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setAlert("Unable to connect with Google", "error");
      setLoading(false);
    },
    hint: survey?.email || undefined,
    scope:
      "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.readonly",
  });

  const handleConnectToGoogle = () => {
    googleLogin();
  };

  return (
    <Grid item xs={12} justifyContent="center" container sx={styles.badge}>
      <Grid item xs={12} justifyContent="center" container>
        <Typography sx={styles.descriptionLargeText}>
          📑
          <br />
          Want{" "}
          <Typography
            component="span"
            sx={[styles.descriptionLargeText, { color: "#F7EE6F" }]}
          >
            The Full Report
          </Typography>{" "}
          of every deal?
        </Typography>
      </Grid>
      <Grid item xs={12} justifyContent="center" container>
        <Typography sx={styles.descriptionText}>
          We'll send you the complete report with brand-by-brand insights and
          growth suggestions
        </Typography>
      </Grid>
      <Grid item xs={12} justifyContent="center" container>
        <Box sx={styles.ctaBox}>
          <Typography
            variant="h4"
            sx={{ textAlign: "center", fontWeight: 600 }}
            paragraph
          >
            Connect your Gmail
          </Typography>
          <Typography sx={{ textAlign: "center", fontSize: "16px" }} paragraph>
            Your Gmail's safe - we just need the deal info.
          </Typography>
          <LoadingButton
            variant="contained"
            color="primary"
            sx={styles.button}
            fullWidth
            loading={loading}
            onClick={handleConnectToGoogle}
          >
            Let's go
          </LoadingButton>
        </Box>
      </Grid>
    </Grid>
  );
}
